import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Plus, Search } from 'lucide-react';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  AdminRole,
  Content,
  ContentConfig,
  ContentWithConfig,
} from '@/types/custom';

import { useContentList } from '@/lib/hooks/use-content';
import AudioCard from '@/components/audio-card';
import { useProfile } from '@/lib/providers';
import MeditationEdit from '@/components/meditation/meditation-edit';
import { Button } from '@/components/ui/button';
import { canSeeBinauralBeats, getDeepLink } from '@/lib/constants';
import { searchContentList } from '@/lib/search';
import EditableDescription from '@/components/editable-description';

/**
 * Audio catalog for binaural beats.
 * (Similar behavior to audio-catalog.tsx)
 * TODO: consolidate into audio-catalog.tsx by adding search and contentTypeDescription support?
 */
export default function BinauralBeatsPage() {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const { favorites, toggleFavorite } = useFavorites();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBeats, setFilteredBeats] = useState<ContentWithConfig[]>([]);

  const { contentWithConfigs: allBeats, loading: dbLoading } = useContentList(
    'binaural',
    [],
  );

  const [editingContent, setEditingContent] = useState<Content | undefined>(
    undefined,
  );
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleSave = () => {
    // Refresh the content list
    window.location.reload();
  };

  // Filter binaural beats based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredBeats(allBeats);
      return;
    }

    const results = searchContentList(
      searchTerm,
      allBeats.map((x) => x.content),
    );

    const filtered = allBeats.filter((beat, i) => results[i]);
    setFilteredBeats(filtered);
  }, [searchTerm, allBeats]);

  /**
   * Begin editing a given audio item, or create a new one if undefined.
   */
  const requestEdit = (
    content: Content | undefined,
    config: ContentConfig | undefined,
  ) => {
    setEditingContent(content);
    setShowEditDialog(true);
  };

  // The section is not released yet, so a stale link or a typed URL goes home
  // rather than into a catalogue nothing navigates to.
  if (!loadingProfile && !canSeeBinauralBeats(isAdmin)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
      <div className="mb-8">
        <div className="space-y-2 mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold">Binaural Beats</h1>

            {isAdmin && (
            <div
              className="relative"
              style={{ marginTop: '12px', marginLeft: '24px' }}
            >
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => requestEdit(undefined, undefined)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Toevoegen
              </Button>
            </div>
          )}
          </div>
          <EditableDescription
            pageKey="binaural-beats"
            defaultText="Binaural beats zijn audiotonen die je hersengolven kunnen beïnvloeden en je helpen bij ontspanning, focus, slaap en meer."
          />
        </div>

        <div className="max-w-xl mb-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-10 w-full rounded-full"
              placeholder="Zoek binaural beats..."
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {searchTerm.trim() !== '' && (
        <div className="mb-4">
          <h3 className="text-xl font-semibold">Zoekresultaten</h3>
          <p className="text-muted-foreground mt-2">
            {filteredBeats.length} resultaten gevonden voor "{searchTerm}"
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 lg:gap-6 px-4 sm:px-0">
        {filteredBeats.map((beat) => (
          <AudioCard
            key={beat.content.id}
            content={beat.content}
            config={beat.configs.at(0)}
            isFavorite={favorites[beat.content.id]}
            onFavoriteToggle={(e) => {
              e.stopPropagation();
              toggleFavorite(beat.content.id);
            }}
            onClick={() => navigate(getDeepLink('binaural', beat.content.id))}
            requestEdit={(e, content, config) => {
              e.stopPropagation();
              requestEdit(content, config);
            }}
          />
        ))}
      </div>

      {/* MeditationEdit Dialog */}
      <MeditationEdit
        content={editingContent}
        onSave={handleSave}
        isOpen={showEditDialog}
        handleClose={() => {
          setShowEditDialog(false);
          setEditingContent(undefined);
        }}
        defaultMeditationType="binaural"
      />
    </div>
  );
}
